import * as React from 'react';
import { IconButton, Tabs, UIHeading2, UIHeading4, UIText1, UIText2 } from '@cian/ui-kit';
import { IconActionClose16, IconFunctionalInformationCircle16, IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';

import {
  IDistrictTaRating,
  IDistrictTransportAccessibility,
  TTaCategory,
  TTaGroups,
  TTaTab,
} from '../../../../types/infrastructure';
import { TA_ICON_MAP, TA_INSIDE_ORDER, TA_OUTSIDE_ORDER, TA_TAB_TITLES, TA_TITLE_MAP } from '../../constants';
import * as styles from './styles.css';

/** Ширина сайдбара транспортной доступности — как в map-search. */
export const TA_SIDEBAR_WIDTH = 338;

export interface ITaPanelProps {
  activeCategory: TTaCategory | null;
  activeTab: TTaTab;
  data: IDistrictTransportAccessibility;
  rating: IDistrictTaRating | null;
  onCategoryChange(category: TTaCategory): void;
  onClose(): void;
  onRatingOpen(): void;
  onSourceClick(): void;
  onTabChange(tab: TTaTab): void;
}

function hasAnyCategory(groups: TTaGroups): boolean {
  return Object.values(groups).some(items => items?.length);
}

export function getTaTabs(data: IDistrictTransportAccessibility): TTaTab[] {
  const tabs: TTaTab[] = [];

  if (hasAnyCategory(data.outside)) {
    tabs.push('outside');
  }
  if (data.inside && hasAnyCategory(data.inside)) {
    tabs.push('inside');
  }

  return tabs;
}

export function formatTaRate(rate: number): string {
  return Math.min(rate, 10).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

/** Сайдбар транспортной доступности: табы, оценка Дептранса и выбор категории точек на карте. */
export function TaPanel({
  activeCategory,
  activeTab,
  data,
  rating,
  onCategoryChange,
  onClose,
  onRatingOpen,
  onSourceClick,
  onTabChange,
}: ITaPanelProps): React.JSX.Element {
  const tabs = getTaTabs(data);
  const groups = (activeTab === 'inside' ? data.inside : data.outside) ?? {};
  const order = activeTab === 'inside' ? TA_INSIDE_ORDER : TA_OUTSIDE_ORDER;
  const availableCategories = order.filter(category => groups[category]?.length);
  const missingCategories = activeTab === 'inside' ? order.filter(category => !groups[category]?.length) : [];
  const tabRating = activeTab === 'inside' ? rating?.infrastructure : rating?.transport;

  const handleTabClick = React.useCallback(
    (index: number) => {
      const tab = tabs[index];

      if (tab && tab !== activeTab) {
        onTabChange(tab);
      }
    },
    [activeTab, tabs, onTabChange],
  );

  return (
    <div
      className={styles['container']}
      data-testid="district-infrastructure-map-ta-panel"
      style={{ '--ta-sidebar-width': `${TA_SIDEBAR_WIDTH}px` } as React.CSSProperties}
    >
      <div className={styles['close-button']}>
        <IconButton
          aria-label="Закрыть"
          data-testid="district-infrastructure-map-ta-close"
          icon={<IconActionClose16 />}
          onClick={onClose}
        />
      </div>
      <div className={styles['header']}>
        <UIHeading2>Транспортная доступность</UIHeading2>
      </div>
      {tabs.length > 1 && (
        <div className={styles['tabs']}>
          <Tabs
            activeTabIndex={Math.max(0, tabs.indexOf(activeTab))}
            items={tabs.map(tab => ({
              dataTestId: `district-infrastructure-map-ta-tab-${tab}`,
              text: TA_TAB_TITLES[tab],
            }))}
            onTabClick={handleTabClick}
          />
        </div>
      )}
      <div className={styles['content']}>
        {tabRating && (
          <button
            className={styles['rate-plate']}
            data-testid="district-infrastructure-map-ta-rate"
            type="button"
            onClick={onRatingOpen}
          >
            <div className={styles['rate-title']}>
              <UIText2 fontWeight="bold">Оценка от Дептранса</UIText2>
              <span className={styles['rate-info-icon']}>
                <IconFunctionalInformationCircle16 color="icon-main-default" />
              </span>
              <span className={styles['rate-value']}>
                <UIText2 fontWeight="bold">{formatTaRate(tabRating.rate)}</UIText2>
              </span>
            </div>
            <div className={styles['rate-bar']}>
              <div className={styles['rate-bar-value']} style={{ width: `${Math.min(tabRating.rate, 10) * 10}%` }} />
            </div>
          </button>
        )}
        <div className={styles['rows']}>
          {availableCategories.map(category => {
            const Icon = TA_ICON_MAP[category];
            const isActive = category === activeCategory;

            return (
              <button
                className={styles['row']}
                data-testid={`district-infrastructure-map-ta-category-${category}`}
                key={category}
                type="button"
                onClick={() => onCategoryChange(category)}
              >
                <Icon color="icon-secondary-default" />
                <span className={styles['row-text']}>
                  <UIText1>{TA_TITLE_MAP[category]}</UIText1>
                  {isActive && (
                    <span className={styles['row-success']}>
                      <IconFunctionalSuccess16 color="icon-main-default" />
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {missingCategories.length > 0 && (
          <div className={styles['rows']} data-testid="district-infrastructure-map-ta-missing">
            <div className={styles['missing-heading']}>
              <UIHeading4>Отсутствует</UIHeading4>
            </div>
            {missingCategories.map(category => {
              const Icon = TA_ICON_MAP[category];

              return (
                <div className={styles['row']} key={category}>
                  <Icon color="icon-primary-disabled" />
                  <span className={styles['row-text']}>
                    <UIText1 color="text-primary-disabled">{TA_TITLE_MAP[category]}</UIText1>
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {rating && (
          <div className={styles['source']}>
            <a
              data-testid="district-infrastructure-map-ta-source"
              href={rating.sourceUrl}
              rel="noreferrer noopener"
              target="_blank"
              onClick={onSourceClick}
            >
              <UIText2 color="text-main-default">Методика Дептранса</UIText2>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
